import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Typography, Button, Empty } from 'antd';
import { RetweetOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { AntdTable, Container } from 'components/custom-table/Style';

const { Title } = Typography;

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 10px;
	}

	.ant-modal-body {
		padding: 24px 32px;
		max-height: 70vh;
		overflow-y: auto;
	}
`;

const ModalHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 24px;
`;

const HeaderIcon = styled.div`
	width: 40px;
	height: 40px;
	border-radius: 8px;
	background: #eff6ff;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #2563eb;
	font-size: 20px;
`;

const SectionTitle = styled(Title)`
	&& {
		margin: 0 0 12px;
		font-size: 16px;
		font-weight: 600;
		color: #1f2937;
	}
`;

const FooterRow = styled.div`
	display: flex;
	justify-content: center;
	margin-top: 28px;
`;

const CloseButton = styled(Button)`
	height: 40px;
	padding: 0 40px;
	background-color: #1e3a8a;
	border-color: #1e3a8a;
	color: #fff;
	font-weight: 500;
	border-radius: 6px;

	&:hover,
	&:focus {
		background-color: #1e40af !important;
		border-color: #1e40af !important;
		color: #fff !important;
	}
`;

const IST_OFFSET_MINUTES = 330; // UTC+5:30

const formatDateTime = (value) => {
	if (!value) return '-';
	const utc = moment.utc(value, [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss'], true);
	if (utc.isValid()) {
		return utc.utcOffset(IST_OFFSET_MINUTES).format('DD-MM-YYYY hh:mm A');
	}
	const local = moment(value, ['DD-MM-YYYY hh:mm a', 'DD-MM-YYYY HH:mm'], true);
	return local.isValid() ? local.format('DD-MM-YYYY hh:mm A') : value;
};

const formatCurrency = (value) => {
	if (value == null || value === '' || value === '-') return '-';
	const num = Number(value);
	if (Number.isNaN(num)) return value;
	return `₹ ${num.toLocaleString('en-IN')}`;
};

const renderValue = (value) => (value == null || value === '' ? '-' : value);

const DEDUCTION_TYPE_LABELS = {
	flat: 'Vendor Rate',
	per_km: 'Charges Per KM',
	per_kilometer: 'Charges Per KM',
	per_order: 'Charges Per Order',
};
const rateBasisKey = (rateBasis) => String(rateBasis || 'flat').toLowerCase();
const renderDeductionType = (_, row) =>
	DEDUCTION_TYPE_LABELS[rateBasisKey(row.rate_basis)] || renderValue(row.rate_basis);
const buildVehicleColumns = () => [
	{ title: 'Trip ID', dataIndex: 'trip_id', width: 130, render: renderValue },
	{ title: 'Old Vendor', dataIndex: 'old_vendor', width: 170, render: renderValue },
	{ title: 'New Vendor', dataIndex: 'new_vendor', width: 170, render: renderValue },
	{
		title: 'Old Vehicle Type',
		dataIndex: 'old_vehicle_type',
		width: 190,
		render: renderValue,
	},
	{
		title: 'New Vehicle Type',
		dataIndex: 'new_vehicle_type',
		width: 190,
		render: renderValue,
	},
	{ title: 'Old Driver', dataIndex: 'old_driver', width: 160, render: renderValue },
	{ title: 'New Driver', dataIndex: 'new_driver', width: 160, render: renderValue },
	{
		title: 'Old Vehicle Number',
		dataIndex: 'initial',
		width: 170,
		render: renderValue,
	},
	{
		title: 'New Vehicle Number',
		dataIndex: 'updated',
		width: 170,
		render: renderValue,
	},
	{
		title: 'Deduction Type',
		dataIndex: 'rate_basis',
		width: 160,
		render: renderDeductionType,
	},
	{ title: 'Old Charges', dataIndex: 'old_price_value', width: 160, render: formatCurrency },
	{ title: 'New Charges', dataIndex: 'new_price_value', width: 160, render: formatCurrency },
	{ title: 'Deduction', dataIndex: 'deduction_amount', width: 140, render: formatCurrency },
	{ title: 'Updated By', dataIndex: 'updated_by', width: 160, render: renderValue },
	{ title: 'Updated At', dataIndex: 'updated_at', width: 180, render: formatDateTime },
	{ title: 'Reason', dataIndex: 'reason', width: 240, render: renderValue },
];

const sumWidth = (cols) => cols.reduce((total, col) => total + (col.width || 150), 0);

const withRowKeys = (rows = []) => rows.map((row, index) => ({ key: row.id ?? index, ...row }));

function VehicleReplacementHistoryModal({ visible, vehicleReplacements, onClose }) {
	const columns = buildVehicleColumns();
	return (
		<StyledModal
			open={visible}
			onCancel={onClose}
			footer={null}
			width={1280}
			destroyOnClose
			closable
		>
			<ModalHeader>
				<HeaderIcon>
					<RetweetOutlined />
				</HeaderIcon>
				<Title level={4} style={{ margin: 0, color: '#1f2937' }}>
					Replacement History
				</Title>
			</ModalHeader>

			<SectionTitle level={5}>Vehicle Replacement</SectionTitle>
			<Container>
				<AntdTable
					columns={columns}
					dataSource={withRowKeys(vehicleReplacements)}
					pagination={false}
					size="middle"
					scroll={{ x: sumWidth(columns), y: 400 }}
					locale={{
						emptyText: <Empty description="No vehicle replacements" />,
					}}
				/>
			</Container>

			<FooterRow>
				<CloseButton onClick={onClose}>Close</CloseButton>
			</FooterRow>
		</StyledModal>
	);
}

VehicleReplacementHistoryModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	vehicleReplacements: PropTypes.arrayOf(PropTypes.object),
	onClose: PropTypes.func.isRequired,
};

VehicleReplacementHistoryModal.defaultProps = {
	vehicleReplacements: [],
};

export default VehicleReplacementHistoryModal;
